import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Dialog from "@mui/material/Dialog";
import InputBase from "@mui/material/InputBase";
import NativeSelect from "@mui/material/NativeSelect";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { type KeyboardEvent, useLayoutEffect, useMemo, useRef, useState } from "react";

import type { OpenTask } from "@storyboard/dispatch-view/task";
import type { DispatchViewDefinition } from "@storyboard/dispatch-view/view";
import { TestIds } from "@storyboard/web-test-ids/testIds";
import {
  driverCountLabel,
  existingTaskLine,
  type TaskAboutChoice,
  taskAboutHeading,
  taskSubmissionFor,
  taskViewOptionsFor,
} from "@/hooks/dispatch/taskOptions";
import type { TaskAbout } from "@/hooks/dispatch/tasks";
import { zinc } from "@/ui/tokens";

/**
 * What the picker is open for: a new task, about one of the subjects offered, or moving an open task. A new task names
 * the drivers it is about, for the About header.
 */
export type TaskPickerMode =
  | { kind: "make"; drivers: readonly string[]; choices: readonly TaskAboutChoice[] }
  | { kind: "move"; task: OpenTask };

/** The board dropdown's test id, on the `select` itself so a test can choose a board by its label. */
const BOARD_TEST_ID = { "data-sb": TestIds.dispatch.taskBoard };

const fieldSx = {
  width: "100%",
  px: 1,
  py: 0.5,
  fontSize: "0.8125rem",
  color: "white",
  bgcolor: zinc[900],
  border: `1px solid ${zinc[600]}`,
  borderRadius: 1,
} as const;

const selectSx = {
  ...fieldSx,
  py: 0,
  "& select": { py: 0.5, color: "white" },
  "& select option": { bgcolor: zinc[900], color: "white" },
  "& svg": { color: zinc[400] },
} as const;

const toggleSx = {
  flex: 1,
  py: 0.25,
  px: 1,
  fontSize: "0.75rem",
  textTransform: "none",
  color: zinc[300],
  borderColor: zinc[600],
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "&.Mui-selected, &.Mui-selected:hover": { color: "white", bgcolor: zinc[600] },
} as const;

const labelSx = { px: 0.5, pt: 1, pb: 0.5, fontSize: "0.75rem", color: zinc[400] } as const;

const headingSx = { px: 0.5, fontSize: "0.8125rem", color: "white", whiteSpace: "nowrap" } as const;

const mutedSx = { px: 0.5, pt: 0.5, fontSize: "0.75rem", color: zinc[400] } as const;

const submitSx = {
  height: 30,
  px: 1.5,
  borderRadius: 1,
  fontSize: "0.8125rem",
  fontWeight: 600,
  bgcolor: "var(--color-brand-primary-600)",
  color: "white",
  "&:hover": { bgcolor: "var(--color-brand-primary-500)" },
  "&:disabled": { opacity: 0.5 },
} as const;

/**
 * The About header: the drivers a new task is about, comma-separated on one line, or "N drivers" when there are more
 * than a few or their names would not fit on it.
 *
 * A hidden copy of the names measures their width against the line's, so a list that would wrap reads as the count.
 */
function TaskAboutDrivers({ names }: { names: readonly string[] }) {
  const heading = taskAboutHeading(names);
  const lineRef = useRef<HTMLDivElement>(null);
  const fullRef = useRef<HTMLSpanElement>(null);
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const line = lineRef.current;
    const full = fullRef.current;
    setOverflows(line !== null && full !== null && full.scrollWidth > line.clientWidth);
  }, [heading]);
  // One driver's long name ends in an ellipsis rather than reading "1 driver".
  const shown = overflows && names.length > 1 ? driverCountLabel(names.length) : heading;
  return (
    <Box ref={lineRef} sx={{ position: "relative", overflow: "hidden" }}>
      <Typography
        ref={fullRef}
        component="span"
        aria-hidden
        sx={{ ...headingSx, position: "absolute", visibility: "hidden" }}
      >
        {heading}
      </Typography>
      <Typography data-sb={TestIds.dispatch.taskAbout} noWrap sx={headingSx}>
        {shown}
      </Typography>
    </Box>
  );
}

/**
 * The task picker: a small form to make a task, or to move an open one to another board.
 *
 * Make mode heads the form with the drivers the task is about, then offers what it is about when there is a choice: the
 * message or event in context, the default, or the driver only. Then the board, from a dropdown that opens with none
 * chosen and lists the viewer's own first, and a note, which is required when the chosen subject needs one. When the
 * chosen message or event already carries an open task, a muted line says so and Submit moves that task instead; the
 * note is then hidden, since a move carries none. Move mode opens on the task's current board and offers "Move", which
 * stays off until another board is chosen. Enter, or Ctrl or Cmd with Enter, submits from anywhere in the form; Shift
 * with Enter keeps a newline in the note, and Escape closes.
 */
export default function DispatchTaskDialog({
  open,
  views,
  currentViewId,
  mode,
  onMake,
  onMove,
  onClose,
}: {
  open: boolean;
  /** Every view the workspace offers, the built-in default included: each is a board. */
  views: readonly DispatchViewDefinition[];
  /** The view the viewer is on, listed first and labeled as theirs. */
  currentViewId: string;
  mode: TaskPickerMode;
  onMake: (about: TaskAbout, viewId: string, note: string | null) => void;
  onMove: (task: OpenTask, viewId: string) => void;
  onClose: () => void;
}) {
  const options = useMemo(() => taskViewOptionsFor({ views, currentViewId }), [views, currentViewId]);
  const pinnedViewId = mode.kind === "move" ? (mode.task.pin?.viewId ?? null) : null;
  const choices = mode.kind === "make" ? mode.choices : [];
  const drivers = mode.kind === "make" ? mode.drivers : [];
  const [about, setAbout] = useState<TaskAbout>(choices[0]?.about ?? "driver");
  const [viewId, setViewId] = useState<string | null>(pinnedViewId);
  const [note, setNote] = useState("");
  const choice = choices.find((candidate) => candidate.about === about) ?? null;
  // A move carries no note, so a subject whose task Submit would move asks for none.
  const showsNote = mode.kind === "make" && !choice?.current;
  const requiresNote = showsNote && (choice?.requiresNote ?? false);

  const submission =
    mode.kind === "move"
      ? taskSubmissionFor({ mode: "move", viewId, pinnedViewId })
      : taskSubmissionFor({ mode: "make", about, viewId, note, requiresNote });

  const submit = () => {
    if (!submission) return;
    if (submission.kind === "make") {
      onMake(submission.about, submission.viewId, submission.note);
      return;
    }
    if (mode.kind === "move") onMove(mode.task, submission.viewId);
  };

  // Enter submits from anywhere in the form, as Ctrl or Cmd with Enter does; Shift with Enter is a newline in the note.
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    event.preventDefault();
    submit();
  };

  const labelFor = (id: string) => views.find((view) => view.id === id)?.label ?? null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      onKeyDown={onKeyDown}
      slotProps={{
        paper: { sx: { width: 360, bgcolor: zinc[800], color: "white", border: `1px solid ${zinc[600]}`, p: 1 } },
      }}
    >
      <Box data-sb={TestIds.dispatch.taskDialog}>
        <Typography sx={{ px: 0.5, pb: 0.25, fontSize: "0.8125rem", fontWeight: 600 }}>
          {mode.kind === "move" ? "Task" : "Make a task"}
        </Typography>
        {(drivers.length > 0 || choices.length > 1) && <Typography sx={labelSx}>About</Typography>}
        {drivers.length > 0 && <TaskAboutDrivers names={drivers} />}
        {choices.length > 1 && (
          <Box sx={{ pt: drivers.length > 0 ? 0.5 : 0 }}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              size="small"
              value={about}
              onChange={(_, next: TaskAbout | null) => {
                if (next !== null) setAbout(next);
              }}
              aria-label="About"
            >
              {choices.map((candidate) => (
                <ToggleButton key={candidate.about} value={candidate.about} sx={toggleSx}>
                  {candidate.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        )}
        <Typography sx={labelSx}>Board</Typography>
        <NativeSelect
          autoFocus
          disableUnderline
          value={viewId ?? ""}
          onChange={(event) => setViewId(event.target.value === "" ? null : event.target.value)}
          inputProps={{ "aria-label": "Board", ...BOARD_TEST_ID }}
          sx={selectSx}
        >
          {viewId === null && (
            <option value="" disabled>
              Choose a board…
            </option>
          )}
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </NativeSelect>
        {choice?.current && <Typography sx={mutedSx}>{existingTaskLine(choice.current, labelFor)}</Typography>}
        {showsNote && (
          <>
            <Typography sx={labelSx}>Note</Typography>
            <InputBase
              multiline
              minRows={2}
              placeholder={requiresNote ? "Note (required)" : "Note"}
              value={note}
              onChange={(event) => setNote(event.target.value)}
              inputProps={{ "aria-label": "Note", "data-sb": TestIds.dispatch.taskNote }}
              sx={fieldSx}
            />
            {requiresNote && note.trim() === "" && (
              <Typography sx={mutedSx}>A driver with nothing waiting needs a note.</Typography>
            )}
          </>
        )}
        <Box sx={{ display: "flex", alignItems: "center", pt: 1 }}>
          <ButtonBase
            data-sb={TestIds.dispatch.taskSubmit}
            onClick={submit}
            disabled={!submission}
            sx={{ ...submitSx, ml: "auto" }}
          >
            {mode.kind === "move" ? "Move" : "Submit"}
          </ButtonBase>
        </Box>
      </Box>
    </Dialog>
  );
}
